import React from 'react';
import { Link } from 'react-router-dom';

import {
  OPTIONAL_HEADERS,
  REQUIRED_HEADERS,
  type RequiredHeader,
} from '@eluve/api-contract';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Heading,
  Icon,
  Separator,
  Stack,
  Tag,
  Text,
} from '@eluve/components';
import { pluralize } from '@eluve/utils';

import { FlowStepper } from '../components/FlowStepper';
import { WhiteCard } from '../components/WhiteCard';
import { useImportJob } from '../hooks/useImportJob';
import {
  PATIENT_IMPORT_ROUTE,
  TEMPLATE_FILENAME,
  downloadTemplateCsv,
} from '../utils';

const HeaderRow: React.FC<{
  name: string;
  index: number;
  status: 'unknown' | 'missing';
}> = ({ name, index, status }) => (
  <Stack
    direction="horizontal"
    gap={3}
    align="center"
    className="border-b border-border-tertiary px-4 py-2 last:border-b-0"
  >
    <Text size="xs" color="tertiary" className="w-6 font-mono tabular-nums">
      {index + 1}
    </Text>
    <Text inline size="sm" className="flex-1 font-mono">
      {name}
    </Text>
    {status === 'unknown' ? (
      <Tag size="xs" color="gray" label="Unknown column" />
    ) : (
      <Tag size="xs" color="negative" label="Missing required" />
    )}
  </Stack>
);

const RequiredHeaderRow: React.FC<{
  name: RequiredHeader;
  isMissing: boolean;
}> = ({ name, isMissing }) => (
  <Stack direction="horizontal" gap={2} align="center">
    {isMissing ? (
      <Icon name="X" size="xs" className="text-text-negative" />
    ) : (
      <Icon name="Check" size="xs" className="text-text-positive-subtle" />
    )}
    <Text
      inline
      size="sm"
      className="flex-1 font-mono"
      color={isMissing ? 'primary' : 'tertiary'}
    >
      {name}
    </Text>
    {isMissing ? <Tag size="xs" color="negative" label="Missing" /> : null}
  </Stack>
);

const OptionalHeaderRow: React.FC<{ name: string }> = ({ name }) => (
  <Stack direction="horizontal" gap={2} align="center">
    <Icon name="Info" size="xs" className="text-text-tertiary" />
    <Text inline size="sm" className="flex-1 font-mono" color="tertiary">
      {name}
    </Text>
  </Stack>
);

export const PatientImportHeaderMismatchScreen: React.FC<{
  jobId: string;
  tenantId: string;
}> = ({ jobId, tenantId }) => {
  const job = useImportJob(tenantId, jobId);
  const missing = (job.missingRequiredHeaders ?? []) as RequiredHeader[];
  const unknown = job.unknownHeaders ?? [];
  const problemRows = [
    ...missing.map((name) => ({ name, status: 'missing' as const })),
    ...unknown.map((name) => ({ name, status: 'unknown' as const })),
  ];

  return (
    <Stack gap={6} align="stretch" className="mx-auto max-w-7xl">
      <Stack direction="horizontal" justify="between" align="start" gap={4}>
        <Stack gap={1}>
          <Heading size="3xl" weight="semibold">
            Your column headers don't match
          </Heading>
          <Stack direction="horizontal" gap={2} align="center">
            <Icon name="FileText" size="xs" />
            <Text inline color="tertiary" size="sm" className="font-mono">
              {job.filename}
            </Text>
            <Text inline color="tertiary" size="sm">
              · just now
            </Text>
          </Stack>
        </Stack>
        <Link to={`${PATIENT_IMPORT_ROUTE}/new`}>
          <Button>
            <Icon name="RefreshCw" size="auto" />
            Reupload file
          </Button>
        </Link>
      </Stack>

      <FlowStepper current="upload" />

      <Alert variant="destructive">
        <Icon name="TriangleAlert" size="md" />
        <AlertTitle>Column names must match the template exactly.</AlertTitle>
        <AlertDescription>
          Rename your file's columns to match the required names, then
          re-upload.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px]">
        <Stack gap={4} align="stretch">
          <WhiteCard className="p-0">
            <Stack gap={0}>
              <Stack
                gap={1}
                className="border-b border-border-tertiary px-4 py-3"
              >
                <Heading size="lg" weight="semibold">
                  What needs to change
                </Heading>
                <Text size="xs" color="tertiary">
                  {pluralize(problemRows.length, 'issue', 'issues')} to fix
                  before re-uploading
                </Text>
              </Stack>
              {problemRows.length === 0 ? (
                <Stack gap={2} align="center" className="px-4 py-8">
                  <Icon
                    name="Check"
                    size="lg"
                    className="text-text-positive-subtle"
                  />
                  <Text color="tertiary">No header issues detected.</Text>
                </Stack>
              ) : (
                <Stack gap={0}>
                  {problemRows.map((row, i) => (
                    <HeaderRow
                      key={`${row.status}-${row.name}`}
                      name={row.name}
                      index={i}
                      status={row.status}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </WhiteCard>
        </Stack>

        <Stack gap={4} align="stretch">
          <WhiteCard>
            <Stack gap={4}>
              <Stack gap={2}>
                <Heading size="lg" weight="semibold">
                  Required headers
                </Heading>
                <Stack gap={1}>
                  {REQUIRED_HEADERS.map((h) => (
                    <RequiredHeaderRow
                      key={h}
                      name={h}
                      isMissing={missing.includes(h)}
                    />
                  ))}
                </Stack>
              </Stack>
              <Separator />
              <Stack gap={2}>
                <Heading size="lg" weight="semibold">
                  Optional headers
                </Heading>
                <Stack gap={1}>
                  {OPTIONAL_HEADERS.map((h) => (
                    <OptionalHeaderRow key={h} name={h} />
                  ))}
                </Stack>
              </Stack>
            </Stack>
          </WhiteCard>

          <WhiteCard>
            <Stack gap={3}>
              <Heading size="lg" weight="semibold">
                Or use our blank template
              </Heading>
              <Text size="xs" color="tertiary">
                It already has the right column headers — just paste your data
                into it.
              </Text>
              <Button
                variant="outline"
                className="w-full justify-center"
                onClick={downloadTemplateCsv}
              >
                <Icon name="Download" size="sm" />
                Download {TEMPLATE_FILENAME}
              </Button>
            </Stack>
          </WhiteCard>
        </Stack>
      </div>
    </Stack>
  );
};
